import React, { useMemo } from 'react'
import { ColDef, ColGroupDef, GetRowIdParams } from 'ag-grid-enterprise'
import CustomAgGrid from '../../../../../shared/components/custom/Grid/CustomAgGrid'
import { CustomReportTableSection } from '../../../../../redux/context/customReports/typesSection'
import {
  createColumnDefinitions,
  createAutoGroupColumnDef,
  calculateAutoGroupWidth
} from '../grid/utils/columnDefinitions'
import { getRowStyle } from '../grid/utils/renderingUtils'
import { useRowFiltering } from '../../hooks/useRowFiltering'
import printTheme from './printTheme'
import { computeHeaderHeight, computeGroupHeaderHeight } from './CategoryTitleVisitor'
import { CreateInnerHeaderComponent } from '../grid/HeaderComponent'
import '../grid/style.css'

interface PrintTableSectionProps {
  section: CustomReportTableSection
  sectionWidth: number
}

const countLeafColumns = (cols: (ColDef | ColGroupDef)[]): number =>
  cols.reduce((n, col) => n + ('children' in col && col.children ? countLeafColumns(col.children) : 1), 0)

const PRINT_DEFAULT_COL_DEF = { minWidth: 0, suppressMovable: true }

const PrintTableSection: React.FC<PrintTableSectionProps> = ({ section, sectionWidth }) => {
  const { isExternalFilterPresent, doesExternalFilterPass } = useRowFiltering(false)
  const { variables, categories = [] } = section

  const rowData = useMemo(() => (variables || []).map(v => ({ ...v, id: v.id?.toString() })), [variables])

  const autoGroupWidth = useMemo(() => {
    const calculated = calculateAutoGroupWidth(variables || [])
    const maxForPrint = Math.round(sectionWidth * 0.23)
    return Math.min(calculated, maxForPrint)
  }, [variables, sectionWidth])

  const nameColumn = useMemo(
    () => ({ ...createAutoGroupColumnDef(), width: autoGroupWidth, cellRenderer: undefined, pinned: undefined }),
    [autoGroupWidth]
  )

  const dataCols = useMemo(() => createColumnDefinitions(categories), [categories])
  const leafCount = useMemo(() => countLeafColumns(dataCols), [dataCols])

  const dataColWidth = useMemo(() => {
    if (leafCount === 0) return 100
    return Math.max(1, (sectionWidth - autoGroupWidth - 2) / leafCount)
  }, [sectionWidth, autoGroupWidth, leafCount])

  const headerHeight = useMemo(() => computeHeaderHeight(categories), [categories])
  const groupHeaderHeight = useMemo(() => computeGroupHeaderHeight(categories), [categories])

  const columnDefs = useMemo(() => {
    const fitToSection = (col: ColDef | ColGroupDef): ColDef | ColGroupDef => {
      if ('children' in col && col.children) {
        return { ...col, children: col.children.map(fitToSection) }
      }
      const leaf = col as ColDef
      return {
        ...leaf,
        flex: undefined,
        width: dataColWidth,
        minWidth: 0,
        headerComponentParams: {
          innerHeaderComponent: CreateInnerHeaderComponent,
          template: `<div class="ag-cell-label-container" role="presentation">
            <div data-ref="eLabel" class="ag-header-cell-label-category" role="presentation">
              <span data-ref="eText" class="ag-header-cell-text" role="columnheader"></span>
            </div>
          </div>`
        },
        headerStyle: { fontSize: 10, lineHeight: 1.3 }
      }
    }
    return [nameColumn, ...dataCols.map(fitToSection)]
  }, [nameColumn, dataCols, dataColWidth])

  const gridContext = useMemo(
    () => ({ reportId: section.reportId, sectionId: section.id, categories }),
    [section, categories]
  )

  return (
    <div style={{ width: '100%', overflow: 'hidden' }}>
      <CustomAgGrid
        className="custom-report-grid"
        theme={printTheme}
        domLayout="print"
        headerHeight={headerHeight}
        groupHeaderHeight={groupHeaderHeight}
        defaultColDef={PRINT_DEFAULT_COL_DEF}
        getRowId={(params: GetRowIdParams) => String(params.data.id)}
        rowData={rowData}
        columnDefs={columnDefs}
        context={gridContext}
        getRowStyle={getRowStyle}
        isExternalFilterPresent={isExternalFilterPresent}
        doesExternalFilterPass={doesExternalFilterPass}
        animateRows={false}
      />
    </div>
  )
}

export default React.memo(PrintTableSection)
